import { BottomSheet, Card, Text, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { alpha, Box, CardContent, DialogContent, RadioGroup, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { RadioField } from "@src/appV2/lib/FormFields/RadioField/RadioField";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { useCallback, useState } from "react";

import goldCoinImage from "./assets/images/goldCoin.png";
import {
  GUARANTEED_PAY_BOTTOM_SHEET_GRADIENT,
  GUARANTEED_PAY_WAITLIST_RESPONSES,
  type GuaranteedPayWaitlistResponse,
} from "./constants";

interface GuaranteedPayWaitlistBottomSheetProps {
  modalState: UseModalState;
  workerId: string;
  onSubmit: (response: GuaranteedPayWaitlistResponse) => void;
}

export function GuaranteedPayWaitlistBottomSheet(props: GuaranteedPayWaitlistBottomSheetProps) {
  const { modalState, workerId, onSubmit } = props;
  const [selectedResponse, setSelectedResponse] = useState<GuaranteedPayWaitlistResponse | "">("");

  useLogEffect(
    APP_V2_USER_EVENTS.GUARANTEED_PAY_WAITLIST_BOTTOM_SHEET_VIEWED,
    { workerId },
    { enabled: modalState.modalIsOpen }
  );

  const handleConfirm = useCallback(() => {
    if (!selectedResponse) {
      return;
    }

    logEvent(APP_V2_USER_EVENTS.GUARANTEED_PAY_WAITLIST_SUBMITTED, {
      workerId,
      response: selectedResponse,
    });

    onSubmit(selectedResponse);
    modalState.closeModal();
  }, [selectedResponse, workerId, onSubmit, modalState]);

  function handleDismiss(): void {
    logEvent(APP_V2_USER_EVENTS.GUARANTEED_PAY_WAITLIST_BOTTOM_SHEET_DISMISSED, {
      workerId,
    });
  }

  function handleClose(): void {
    handleDismiss();
    modalState.closeModal();
  }

  const handleResponseChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedResponse(event.target.value as GuaranteedPayWaitlistResponse);
  }, []);

  return (
    <BottomSheet
      modalState={modalState}
      paperSx={{
        minHeight: "90%",
        background: GUARANTEED_PAY_BOTTOM_SHEET_GRADIENT,
        overflow: "hidden",
      }}
      header={
        <BottomSheetHeader
          onClose={() => {
            handleClose();
          }}
        />
      }
      footer={
        <DialogFooter orientation="vertical">
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!selectedResponse}
            onClick={handleConfirm}
          >
            Confirm Selection
          </Button>
        </DialogFooter>
      }
      onClose={() => {
        handleDismiss();
      }}
    >
      <Box
        component="img"
        src={goldCoinImage}
        alt=""
        sx={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: "70%",
          maxWidth: 320,
          aspectRatio: "1 / 1",
          objectFit: "contain",
          pointerEvents: "none",
          transform: "translate(20%, -25%)",
          opacity: 0.75,
        }}
      />
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", flex: 1, position: "relative" }}
      >
        <Stack gap={6}>
          <Text
            semibold
            variant="h2"
            sx={(theme) => ({ textAlign: "center", color: theme.palette.text.primary })}
          >
            Stop losing money to cancellations.
          </Text>

          <Text
            variant="body1"
            sx={(theme) => ({ textAlign: "center", color: theme.palette.text.secondary })}
          >
            Coming soon{" "}
            <Text bold inline sx={(theme) => ({ color: theme.palette.text.primary })}>
              for a small fee
            </Text>
            , you can book shifts with Guaranteed Pay:{" "}
            <Text bold inline sx={(theme) => ({ color: theme.palette.text.primary })}>
              get paid 100% of your wages
            </Text>
            , even if a facility cancels within 24 hours of shift start.
          </Text>
        </Stack>

        <Stack sx={{ flex: 1, justifyContent: "center", marginY: 8 }}>
          <Card
            variant="tertiary"
            sx={(theme) => ({
              ...(isDefined(theme.palette.background.tertiary) && {
                backgroundColor: alpha(theme.palette.background.tertiary, 0.8),
              }),
            })}
          >
            <CardContent>
              <Stack spacing={3}>
                <Text semibold variant="h6">
                  Interested in Guaranteed Pay?
                </Text>
                <Text variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
                  If so, we&apos;ll notify you when it&apos;s available.
                </Text>
                <RadioGroup value={selectedResponse} onChange={handleResponseChange}>
                  <RadioField
                    value={GUARANTEED_PAY_WAITLIST_RESPONSES.INTERESTED}
                    label={
                      <Text sx={(theme) => ({ color: theme.palette.intent?.success?.text })}>
                        Yes! I am interested!
                      </Text>
                    }
                  />
                  <RadioField
                    value={GUARANTEED_PAY_WAITLIST_RESPONSES.NOT_INTERESTED}
                    label={
                      <Text sx={(theme) => ({ color: theme.palette.intent?.error?.text })}>
                        No. I am not interested
                      </Text>
                    }
                  />
                </RadioGroup>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
